import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import { styled } from '@mui/system'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { storageGet, storageSet } from '../../../../utils/storage'
import { useDispatch } from 'react-redux'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../utils/sorting'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import { UspConfigurationLotViewResult } from '../../../../models'
import { useDeleteLotsMutation } from '../../../../services'
import { enqueueNotification } from '../../../../redux/slices/notificationsSlice'
import { CustomCircular } from '../../../../ui'
import Iconify from '../../../../components/iconify'
import CustomPopover from '../../../../components/custom-popover/custom-popover'
import { usePopover } from '../../../../components/custom-popover'
import Label from '../../../../components/label/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'loading', 'tableHead', 'tableCellName', 'inactiveItem', 'lastColumn'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.lastColumn}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
    width: '80px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))

interface Props {
  data: UspConfigurationLotViewResult[]
  loading: boolean
  setData: Dispatch<SetStateAction<UspConfigurationLotViewResult[]>>
  handleOpenModal: () => void
  setOpenAddEditModal: Dispatch<SetStateAction<boolean>>
  setType: Dispatch<SetStateAction<string>>
  setRowData: Dispatch<SetStateAction<UspConfigurationLotViewResult>>
}
const AdminLotsTable = ({ data, loading, setData, setOpenAddEditModal, setType, setRowData }: Props) => {
  const [deleteLot, { isSuccess: deleteIsSuccess }] = useDeleteLotsMutation()
  const initialOrder = (storageGet('lots_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('lots_order_by') || 'Lot') as keyof UspConfigurationLotViewResult
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof UspConfigurationLotViewResult>(initialOrderBy)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ lotId: string } | undefined>(undefined)
  const dispatch = useDispatch()
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(25)
  const paginatedData = data

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspConfigurationLotViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('lots_order', isAsc ? 'desc' : 'asc')
    storageSet('lots_order_by', property)
  }

  const createSortHandler = (property: keyof UspConfigurationLotViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.lotId) {
      await deleteLot(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  const handleOpen = () => {
    setType('edit')
    setOpenAddEditModal(true)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.lotId) {
      if (deleteIsSuccess) {
        let updatedData: UspConfigurationLotViewResult[]
        updatedData = data.map((item) => (item.ID?.toString() === deleteConfirmDialog.lotId ? { ...item, IsActive: false } : item))
        setData(updatedData)
        dispatch(enqueueNotification({ message: 'Success' }))
      }
    }
  }, [deleteIsSuccess])

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'Lot' ? order : false}>
                <TableSortLabel active={orderBy === 'Lot'} direction={orderBy === 'Lot' ? order : 'asc'} onClick={createSortHandler('Lot')}>
                  Name
                  {orderBy === 'Lot' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'BranchCode' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'BranchCode'}
                  direction={orderBy === 'BranchCode' ? order : 'asc'}
                  onClick={createSortHandler('BranchCode')}
                >
                  Branch
                  {orderBy === 'BranchCode' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.lastColumn} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.lastColumn} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), paginatedData as any)
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              ?.map((item) => (
                <TableRow key={item.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell
                    className={classes.tableCellName}
                    onClick={() => {
                      handleOpen()
                      setRowData(item as any)
                    }}
                  >
                    {item.Lot}
                  </TableCell>
                  <TableCell>{item?.BranchCode}</TableCell>
                  <TableCell>
                    <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                  </TableCell>
                  <TableCell align={'right'}>
                    <IconButton
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        setSelected(item)
                        popover.onOpen(event)
                      }}
                    >
                      <Iconify icon='eva:more-vertical-fill' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ width: '100%', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
        <Paper>
          <TablePagination
            component='div'
            count={data?.length as number}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      <CustomCircular open={loading} />
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Are you sure you wish to delete?'}
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          onClick={(e) => {
            handleOpen()
            setRowData(selected as any)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteConfirmDialog({ lotId: selected.ID?.toString() as string })
            popover.onClose()
          }}
          disabled={selected?.IsActive ? false : true}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default AdminLotsTable
